import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import {
  orderService,
  bffConfigService,
  tableService,
  zoneService,
  productService,
  inventoryService,
  bookingService,
  type FoodOrderType,
  type FoodOrder,
  type CreateOrderInput,
  type CreateOrderItemInput,
  type RestaurantTable,
  type Product,
  type InventoryItem,
  type Booking,
  type ModifierGroup,
  type ModifierOption,
  CATEGORY_TYPES,
  CATEGORY_TYPE_LABELS,
  mealPackageService,
  type MealPackage,
} from '@resort-os/api-client';
import { useFormatCurrency, useSnackbar, usePermission, PageSection } from '@resort-os/ui';
import ModifierPicker from '../components/ModifierPicker';
import DraggablePanel from '../components/DraggablePanel';
import MenuLeaves from '../components/MenuLeaves';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Divider,
  CircularProgress,
  Chip,
  InputAdornment,
  IconButton,
  Popover,
  Paper,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from '@mui/material';
import {
  Kitchen as KitchenIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  LocalDining as DiningIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as CartIcon,
  Warning as WarningIcon,
  DragIndicator as DragIcon,
  Minimize as MinimizeIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Coffee as CoffeeIcon,
  Storefront as StorefrontIcon,
  Build as BuildIcon,
  RestaurantMenu as RestaurantMenuIcon,
  Receipt as ReceiptIcon,
  NoteAdd as NoteAddIcon,
  Notes as NotesIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useCart, CartItem } from '../contexts/CartContext';

const ACCENT = '#2C3E2D';
const BORDER = '1px solid #E2DDD8';
const MENU_SCREEN = 12;

const PRODUCT_GROUPS: {
  id: (typeof CATEGORY_TYPES)[number];
  name: string;
  icon: React.ElementType;
  color: string;
}[] = [
  { id: 'beverage', name: CATEGORY_TYPE_LABELS.beverage, icon: CoffeeIcon, color: '#2E7D32' },
  { id: 'food', name: CATEGORY_TYPE_LABELS.food, icon: KitchenIcon, color: '#ED6C02' },
  {
    id: 'merchandise',
    name: CATEGORY_TYPE_LABELS.merchandise,
    icon: StorefrontIcon,
    color: '#0288D1',
  },
  { id: 'service', name: CATEGORY_TYPE_LABELS.service, icon: BuildIcon, color: '#9C27B0' },
  {
    id: 'meal_packages',
    name: CATEGORY_TYPE_LABELS.meal_packages,
    icon: RestaurantMenuIcon,
    color: '#E65100',
  },
];

interface CartItemWithNote extends CartItem {
  note?: string;
  modifiers?: { groupName: string; options: { name: string; price_modifier: number }[] }[];
}

interface OrderFormData {
  orderType: FoodOrderType;
  selectedTable: string;
  selectedBooking: string;
  guestName: string;
  guestCount: number;
  deliveryAddress: string;
  notes: string;
}

function OrdersPage({ PageHeader }: { PageHeader: React.FC<{ title: string }> }) {
  const { items, addItem, removeItem, updateQuantity, clearCart, updateItemNote } = useCart();
  const queryClient = useQueryClient();
  const formatCurrency = useFormatCurrency();
  const { showSnackbar } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeGroup, setActiveGroup] = useState<string>('beverage');
  const [menuLeaf, setMenuLeaf] = useState(0);
  const [isPanelMinimized, setIsPanelMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [noteAnchorEl, setNoteAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedItemForNote, setSelectedItemForNote] = useState<CartItemWithNote | null>(null);
  const [tempNote, setTempNote] = useState('');
  const [addToExistingOrder, setAddToExistingOrder] = useState(false);
  const [selectedExistingOrderId, setSelectedExistingOrderId] = useState('');
  const [existingOrderDetails, setExistingOrderDetails] = useState<FoodOrder | null>(null);
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [chargeToRoom, setChargeToRoom] = useState(false);
  const [modifierPickerOpen, setModifierPickerOpen] = useState(false);
  const [modifierProduct, setModifierProduct] = useState<Product | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  const { control, handleSubmit, watch, setValue } = useForm<OrderFormData>({
    defaultValues: {
      orderType: 'dine_in',
      selectedTable: '',
      selectedBooking: '',
      guestName: '',
      guestCount: 1,
      deliveryAddress: '',
      notes: '',
    },
  });

  const watchedOrderType = watch('orderType');
  const watchedBooking = watch('selectedBooking');

  const { data: configsData } = useQuery({
    queryKey: ['configs'],
    queryFn: () => bffConfigService.getAll(),
  });

  const configs = (configsData as any)?.configs ?? [];
  const roomChargeEnabled = useMemo(() => {
    const c = configs.find((x: any) => x.key === 'feature.room_charge');
    return c ? c.value === 'true' || c.value === true : false;
  }, [configs]);

  const { data: zonesData, isLoading: isLoadingZones } = useQuery({
    queryKey: ['zones'],
    queryFn: () => zoneService.list(),
  });

  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.list({ page_size: 100 }),
  });

  const { data: inventoryData } = useQuery({
    queryKey: ['inventory-stock'],
    queryFn: () => inventoryService.list({ page_size: 200 }),
    refetchInterval: 30000,
  });

  const { data: tablesData, isLoading: isLoadingTables } = useQuery({
    queryKey: ['tables-available', watchedOrderType],
    queryFn: () => tableService.list({ status: 'available' }),
    enabled: watchedOrderType === 'dine_in',
  });

  const { data: activeBookingsData } = useQuery({
    queryKey: ['bookings-active'],
    queryFn: () => bookingService.listActive({ page_size: 100 }),
    enabled: watchedOrderType === 'dine_in',
  });

  const { data: incompleteOrdersData } = useQuery({
    queryKey: ['orders', 'incomplete'],
    queryFn: () => orderService.list({ page: 1, page_size: 100 }),
  });

  const products: Product[] = productsData?.data || [];
  const inventoryItems: InventoryItem[] = inventoryData?.data || [];

  const { data: mealPkgsData } = useQuery({
    queryKey: ['meal-packages'],
    queryFn: () => mealPackageService.list(),
  });
  const mealPackages: MealPackage[] = mealPkgsData?.meal_packages ?? [];
  const tables: RestaurantTable[] = tablesData?.data?.data || [];
  const activeBookings: Booking[] = activeBookingsData?.bookings || [];
  const incompleteOrders: FoodOrder[] = (incompleteOrdersData?.data?.data || []).filter(
    (o: FoodOrder) => !['completed', 'cancelled'].includes(o.status) && o.payment_status !== 'paid',
  );

  // All useMemo hooks
  const filteredIncompleteOrders = useMemo(() => {
    if (!orderSearchQuery) return incompleteOrders;
    const query = orderSearchQuery.toLowerCase();
    return incompleteOrders.filter(
      order =>
        order.order_number.toLowerCase().includes(query) ||
        (order.guest_name && order.guest_name.toLowerCase().includes(query)),
    );
  }, [incompleteOrders, orderSearchQuery]);

  const stockByProductId = useMemo(() => {
    const map: Record<string, { quantity: number; minQty: number; reorderPoint: number }> = {};
    for (const item of inventoryItems) {
      if (item.product_id) {
        map[item.product_id] = {
          quantity: item.current_quantity,
          minQty: item.minimum_quantity,
          reorderPoint: item.reorder_point,
        };
      }
    }
    for (const product of products) {
      if (!map[product.id] && product.stock_item_id) {
        const stockItem = inventoryItems.find(item => item.id === product.stock_item_id);
        if (stockItem) {
          map[product.id] = {
            quantity: stockItem.current_quantity,
            minQty: stockItem.minimum_quantity,
            reorderPoint: stockItem.reorder_point,
          };
        }
      }
    }
    return map;
  }, [inventoryItems, products]);

  const recipeStockByProductId = useMemo(() => {
    const map: Record<
      string,
      { minServings: number; minQty: number; reorderPoint: number; depletedIds: string[] }
    > = {};
    for (const product of products) {
      if (!product.has_recipe || !product.recipes?.length) continue;
      let minServings = Infinity;
      let minQty = Infinity;
      let reorderPoint = 0;
      const depletedIds: string[] = [];
      for (const recipe of product.recipes) {
        const stockItem = inventoryItems.find(item => item.id === recipe.ingredient_stock_id);
        if (!stockItem) {
          minServings = 0;
          depletedIds.push(recipe.ingredient_stock_id);
          continue;
        }
        const servings =
          recipe.quantity_required > 0
            ? Math.floor(stockItem.current_quantity / recipe.quantity_required)
            : 0;
        if (servings <= 0) depletedIds.push(recipe.ingredient_stock_id);
        if (servings < minServings) minServings = servings;
        if (stockItem.minimum_quantity < minQty) minQty = stockItem.minimum_quantity;
        reorderPoint += stockItem.reorder_point;
      }
      map[product.id] = {
        minServings: minServings === Infinity ? 0 : minServings,
        minQty: minQty === Infinity ? 0 : minQty,
        reorderPoint,
        depletedIds,
      };
    }
    return map;
  }, [products, inventoryItems]);

  const groupedProducts = useMemo(() => {
    const groups: Record<string, Product[]> = Object.fromEntries(CATEGORY_TYPES.map(t => [t, []]));

    products.forEach((product: Product) => {
      const categoryType =
        (product.category?.category_type?.toLowerCase() as (typeof CATEGORY_TYPES)[number]) ||
        'food';
      if (groups[categoryType]) {
        groups[categoryType].push(product);
      } else {
        (groups.food || []).push(product);
      }
    });

    return groups;
  }, [products]);

  const filteredProducts = useMemo(() => {
    let filtered = (groupedProducts[activeGroup] || []).filter(
      (p: Product) => p.status === 'available',
    );
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (product: Product) =>
          product.name.toLowerCase().includes(query) ||
          product.category?.name?.toLowerCase().includes(query),
      );
    }
    return filtered;
  }, [groupedProducts, activeGroup, searchQuery]);

  const menuScreens = useMemo(() => {
    const screens: Product[][] = [];
    for (let i = 0; i < filteredProducts.length; i += MENU_SCREEN) {
      screens.push(filteredProducts.slice(i, i + MENU_SCREEN));
    }
    return screens;
  }, [filteredProducts]);

  useEffect(() => {
    setMenuLeaf(0);
  }, [activeGroup, searchQuery]);

  useEffect(() => {
    if (menuScreens.length < 2 || activeGroup === 'meal_packages') return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      setMenuLeaf(current => {
        if (event.key === 'ArrowLeft') return Math.max(0, current - 1);
        return Math.min(menuScreens.length - 1, current + 1);
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuScreens.length, activeGroup]);

  const subtotal = useMemo(() => {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [items]);

  const addingToLegacyOrder = Boolean(
    existingOrderDetails && existingOrderDetails.metadata?.prices_include_vat !== true,
  );

  const taxAmount = useMemo(() => {
    return items.reduce((sum, item) => {
      const gross = item.price * item.quantity;
      const rate = item.tax_rate > 0 ? item.tax_rate : 16;
      if (addingToLegacyOrder) return sum + gross * (rate / 100);
      return sum + (gross * rate) / (100 + rate);
    }, 0);
  }, [items, addingToLegacyOrder]);

  const serviceCharge = addingToLegacyOrder ? 0 : subtotal * 0.1;
  const totalAmount = addingToLegacyOrder ? subtotal + taxAmount : subtotal + serviceCharge;

  const existingOrderCurrentTotal = existingOrderDetails?.total_amount ?? 0;

  const newTotalAfterAddition = existingOrderCurrentTotal + totalAmount;

  const createOrderMutation = useMutation({
    mutationFn: async (data: CreateOrderInput) => {
      const res = await orderService.create(data);
      if (chargeToRoom && data.booking_id) {
        await orderService.chargeToRoom(res.data.id);
      }
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      showSnackbar(
        `Order created ${chargeToRoom ? 'and charged to room ' : ''}successfully`,
        'success',
      );
      clearCart();
      setValue('guestName', '');
      setValue('guestCount', 1);
      setValue('deliveryAddress', '');
      setValue('notes', '');
      setValue('selectedTable', '');
      setValue('selectedBooking', '');
      setChargeToRoom(false);
    },
    onError: error => {
      console.error('Failed to create order:', error);
      showSnackbar('Failed to create order. Please try again.', 'error');
    },
  });

  const addItemToOrderMutation = useMutation({
    mutationFn: ({ orderId, item }: { orderId: string; item: CreateOrderItemInput }) =>
      orderService.addItem(orderId, item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      clearCart();
      setAddToExistingOrder(false);
      setSelectedExistingOrderId('');
      setExistingOrderDetails(null);
      setOrderSearchQuery('');
    },
    onError: error => {
      console.error('Failed to add item:', error);
      showSnackbar('Failed to add item to order. Please try again.', 'error');
    },
  });

  const toggleAvailabilityMutation = useMutation({
    mutationFn: (id: string) => productService.toggleAvailability(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handleDragMove);
      window.addEventListener('pointerup', handleDragEnd);
      return () => {
        window.removeEventListener('pointermove', handleDragMove);
        window.removeEventListener('pointerup', handleDragEnd);
      };
    }
  }, [isDragging]);
  // Loading state
  const isLoading = isLoadingZones || isLoadingProducts || isLoadingTables;

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  // Event handlers
  const handleExistingOrderChange = (orderId: string) => {
    setSelectedExistingOrderId(orderId);
    const order = incompleteOrders.find(o => o.id === orderId);
    setExistingOrderDetails(order || null);
  };

  const onCreateOrder = (data: OrderFormData) => {
    if (items.length === 0) return;

    const orderItems: CreateOrderItemInput[] = items.map(item => ({
      product_id: item.id,
      product_name: item.name,
      quantity: item.quantity,
      unit_price: item.price,
      tax_rate: item.tax_rate,
      department: item.department,
      modifiers: item.modifiers
        ? item.modifiers.flatMap(g =>
            g.options.map(o => ({ name: `${g.groupName}: ${o.name}`, price: o.price_modifier })),
          )
        : undefined,
      preparation_notes: item.note || undefined,
    }));

    const selectedTableData = tables.find(t => t.id === data.selectedTable);
    const orderInput: CreateOrderInput = {
      order_type: data.orderType,
      booking_id:
        data.orderType === 'dine_in' && data.selectedBooking ? data.selectedBooking : undefined,
      table_id: data.orderType === 'dine_in' && data.selectedTable ? data.selectedTable : undefined,
      table_number:
        data.orderType === 'dine_in' && selectedTableData
          ? selectedTableData.table_number
          : undefined,
      guest_count: data.orderType === 'dine_in' ? Number(data.guestCount) : undefined,
      guest_name: data.guestName || undefined,
      delivery_address: data.orderType === 'delivery' ? data.deliveryAddress : undefined,
      items: orderItems,
      notes: data.notes || undefined,
    };

    createOrderMutation.mutate(orderInput);
  };

  const onAddToExistingOrder = () => {
    if (items.length === 0 || !selectedExistingOrderId) return;

    // Create a single promise that adds all items
    const addPromises = items.map(item => {
      const itemInput: CreateOrderItemInput = {
        product_id: item.id,
        product_name: item.name,
        quantity: item.quantity,
        unit_price: item.price,
        tax_rate: item.tax_rate,
        department: item.department,
        modifiers: item.modifiers
          ? item.modifiers.flatMap(g =>
              g.options.map(o => ({ name: `${g.groupName}: ${o.name}`, price: o.price_modifier })),
            )
          : undefined,
        preparation_notes: item.note || undefined,
      };
      return addItemToOrderMutation.mutateAsync({
        orderId: selectedExistingOrderId,
        item: itemInput,
      });
    });

    Promise.all(addPromises)
      .then(() => {
        // All items added successfully
      })
      .catch(error => {
        console.error('Failed to add some items:', error);
        alert('Some items failed to add. Please check the order.');
      });
  };

  const handleProductClick = (product: Product) => {
    const stock = stockByProductId[product.id];
    const recipe = recipeStockByProductId[product.id];
    if (stock && stock.quantity <= 0) return;
    if (recipe && recipe.minServings <= 0) return;

    const hasModifiers =
      product.modifier_groups && (product.modifier_groups as ModifierGroup[]).length > 0;

    if (hasModifiers) {
      setModifierProduct(product);
      setModifierPickerOpen(true);
      return;
    }

    addProductToCart(product);
  };

  const handleModifierConfirm = (
    selectedOptions: { groupName: string; options: ModifierOption[] }[],
  ) => {
    if (!modifierProduct) return;
    addProductToCart(modifierProduct, selectedOptions);
  };

  const addProductToCart = (
    product: Product,
    modifiers?: { groupName: string; options: ModifierOption[] }[],
  ) => {
    const modifierPrice = modifiers
      ? modifiers.reduce(
          (sum, group) => sum + group.options.reduce((s, o) => s + o.price_modifier, 0),
          0,
        )
      : 0;
    const cartItem: CartItem = {
      id: product.id,
      name: product.name,
      price: product.price + modifierPrice,
      quantity: 1,
      tax_rate: product.tax_rate ?? 0,
      department: product.category?.category_type as CartItem['department'],
      note: '',
      modifiers: modifiers || undefined,
    };
    addItem(cartItem);
  };

  const getStockStatus = (productId: string) => {
    const stock = stockByProductId[productId];
    const recipe = recipeStockByProductId[productId];
    if (stock) {
      if (stock.quantity <= 0)
        return { level: 'depleted' as const, label: 'Out of stock', color: '#EF9A9A' };
      if (stock.quantity <= stock.minQty)
        return {
          level: 'critical' as const,
          label: `Only ${stock.quantity} left`,
          color: '#EF9A9A',
        };
      if (stock.quantity <= stock.reorderPoint)
        return { level: 'low' as const, label: `Low: ${stock.quantity}`, color: '#FFB74D' };
      return { level: 'ok' as const, label: '', color: '#4CAF50' };
    }
    if (recipe) {
      if (recipe.minServings <= 0)
        return { level: 'depleted' as const, label: 'Ingredients out', color: '#EF9A9A' };
      if (recipe.minServings <= 2)
        return {
          level: 'critical' as const,
          label: `Only ${recipe.minServings} servings`,
          color: '#EF9A9A',
        };
      if (recipe.depletedIds.length > 0)
        return { level: 'low' as const, label: 'Some ingredients low', color: '#FFB74D' };
      return { level: 'ok' as const, label: '', color: '#4CAF50' };
    }
    return { level: 'unknown' as const, label: '', color: '#9B958F' };
  };

  const handleOpenNotePopover = (event: React.MouseEvent<HTMLElement>, item: CartItemWithNote) => {
    event.stopPropagation();
    setSelectedItemForNote(item);
    setTempNote(item.note || '');
    setNoteAnchorEl(event.currentTarget);
  };

  const handleCloseNotePopover = () => {
    setNoteAnchorEl(null);
    setSelectedItemForNote(null);
    setTempNote('');
  };

  const handleSaveNote = () => {
    if (selectedItemForNote) {
      updateItemNote(selectedItemForNote.id, tempNote);
      handleCloseNotePopover();
    }
  };

  const handleDragStart = (e: React.PointerEvent) => {
    const panel = document.getElementById('floating-panel');
    if (!panel) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: panel.offsetLeft,
      startTop: panel.offsetTop,
    };
    e.preventDefault();
  };

  const handleDragMove = (e: PointerEvent) => {
    if (!isDragging) return;
    const panel = document.getElementById('floating-panel');
    if (!panel) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const newLeft = Math.max(
      0,
      Math.min(window.innerWidth - panel.offsetWidth - 16, dragRef.current.startLeft + dx),
    );
    const newTop = Math.max(
      0,
      Math.min(window.innerHeight - panel.offsetHeight - 16, dragRef.current.startTop + dy),
    );
    panel.style.left = `${newLeft}px`;
    panel.style.top = `${newTop}px`;
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const safeMenuLeaf = Math.min(menuLeaf, Math.max(menuScreens.length - 1, 0));
  const shownProducts = menuScreens.length > 1 ? menuScreens[safeMenuLeaf] || [] : filteredProducts;

  return (
    <Box
      sx={
        isFullscreen
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              bgcolor: '#FAF9F7',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }
          : {
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#FAF9F7',
              position: 'relative',
            }
      }
    >
      <PageSection>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <PageHeader title="Products" />
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} arrow>
            <IconButton
              size="small"
              onClick={() => setIsFullscreen(!isFullscreen)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 0,
                border: '1px solid #E4DFD9',
                color: '#6B6560',
                bgcolor: '#F5F3F0',
                '&:hover': { bgcolor: ACCENT, color: '#fff', borderColor: ACCENT },
              }}
            >
              {isFullscreen ? (
                <FullscreenExitIcon sx={{ fontSize: 16 }} />
              ) : (
                <FullscreenIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Left of the 500px order panel so its Minimize control stays in view. */}
        <DraggablePanel title="Filters" panelWidth={170} initialRight={536}>
          {PRODUCT_GROUPS.map(group => {
            const Icon = group.icon;
            const isActive = activeGroup === group.id;
            const count =
              group.id === 'meal_packages'
                ? mealPackages.length
                : groupedProducts[group.id]?.length || 0;

            return (
              <Box
                key={group.id}
                onClick={() => setActiveGroup(group.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  bgcolor: isActive ? `${group.color}15` : 'transparent',
                  border: isActive ? `1px solid ${group.color}40` : '1px solid transparent',
                  '&:hover': {
                    bgcolor: `${group.color}08`,
                  },
                }}
              >
                <Box
                  data-tour="category-sidebar"
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    bgcolor: isActive ? group.color : '#F5F5F5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Icon sx={{ fontSize: 17, color: isActive ? '#FFF' : '#9B958F' }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: isActive ? group.color : '#1A1A1A',
                      lineHeight: 1.2,
                    }}
                  >
                    {group.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: '#9B958F',
                    }}
                  >
                    {count} items
                  </Typography>
                </Box>
                {isActive && (
                  <Box
                    sx={{
                      width: 3,
                      height: 20,
                      borderRadius: 1.5,
                      bgcolor: group.color,
                      alignSelf: 'stretch',
                      my: -0.5,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </DraggablePanel>

        <Box
          data-tour="search-products"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 0,
            py: 1.5,
            borderBottom: '1px dashed #E2DDD8',
            bgcolor: 'transparent',
          }}
        >
          <TextField
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#9B958F' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 200,
              '& .MuiOutlinedInput-root': { borderRadius: 0 },
              '& .MuiInputBase-input': {
                fontSize: '0.8rem',
              },
            }}
          />
          {activeGroup !== 'meal_packages' && menuScreens.length > 1 && (
            <MenuLeaves count={menuScreens.length} active={safeMenuLeaf} onSelect={setMenuLeaf} />
          )}
        </Box>

        <Box data-tour="product-grid" sx={{ flex: 1, overflow: 'auto', pt: 2, px: 0 }}>
          {activeGroup === 'meal_packages' ? (
            mealPackages.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '50%',
                }}
              >
                <Typography sx={{ color: '#999' }}>No meal packages created</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                  gridAutoRows: { xs: 'auto', lg: '160px' },
                  gap: '2px',
                }}
              >
                {mealPackages.map((mp, idx) => {
                  const span2 = idx % 4 === 0;
                  const isTall = span2;
                  return (
                    <Box
                      key={mp.id}
                      onClick={() => {
                        const ids = Array.isArray(mp.product_ids) ? mp.product_ids : [];
                        const items = products.filter(p => ids.includes(p.id));
                        items.forEach(p => handleProductClick(p));
                      }}
                      sx={{
                        p: 2,
                        border: BORDER,
                        cursor: 'pointer',
                        bgcolor: '#FFF',
                        borderRadius: 1,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gridRow: { lg: span2 ? 'span 2' : 'span 1' },
                        minHeight: { xs: 140, lg: 'auto' },
                        '&:hover': {
                          border: `1px solid ${ACCENT}`,
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(44, 62, 45, 0.1)',
                        },
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: 'rgba(44, 62, 45, 0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <RestaurantMenuIcon sx={{ fontSize: 18, color: ACCENT }} />
                          </Box>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          {mp.name}
                        </Typography>
                        {mp.description && (
                          <Typography
                            sx={{
                              fontSize: '0.7rem',
                              color: '#9B958F',
                              mb: 1,
                            }}
                          >
                            {mp.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(Array.isArray(mp.product_ids) ? mp.product_ids : [])
                            .slice(0, 4)
                            .map(pid => {
                              const p = products.find(pr => pr.id === pid);
                              return p ? (
                                <Chip
                                  key={pid}
                                  label={p.name}
                                  size="small"
                                  sx={{
                                    borderRadius: 0,

                                    fontSize: '0.6rem',
                                    height: 20,
                                    bgcolor: '#E8F5E9',
                                    color: ACCENT,
                                  }}
                                />
                              ) : null;
                            })}
                          {((mp.product_ids as string[])?.length || 0) > 4 && (
                            <Typography
                              sx={{
                                fontSize: '0.65rem',
                                color: '#9B958F',
                                alignSelf: 'center',
                              }}
                            >
                              +{((mp.product_ids as string[])?.length || 0) - 4} more
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mt: 1.5,
                          pt: 1,
                          borderTop: '1px solid #F5F4F2',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AddIcon sx={{ fontSize: 14, color: ACCENT }} />
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: ACCENT,
                            }}
                          >
                            Add all to order
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: ACCENT,
                          }}
                        >
                          {formatCurrency(
                            (Array.isArray(mp.product_ids) ? mp.product_ids : []).reduce(
                              (sum, pid) => {
                                const p = products.find(pr => pr.id === pid);
                                return sum + (p?.price ?? 0);
                              },
                              0,
                            ),
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )
          ) : filteredProducts.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50%',
              }}
            >
              <Typography sx={{ color: '#999' }}>No menu items available</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gridAutoRows: { xs: 'auto', lg: '160px' },
                gap: '2px',
              }}
            >
              {shownProducts.map((product, idx) => {
                const stockStatus = getStockStatus(product.id);
                const isDepleted = stockStatus.level === 'depleted';
                if (isDepleted) return null;
                const isLowStock = stockStatus.level === 'low' || stockStatus.level === 'critical';
                const span2 = idx % 4 === 0;
                const isTall = span2;

                return (
                  <Box
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className="card"
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      border: isLowStock ? `2px solid ${stockStatus.color}` : `1px solid #E4DFD9`,
                      cursor: 'pointer',
                      bgcolor: isLowStock ? '#FFF8F0' : '#FFF',
                      transition: 'all 0.15s',
                      borderRadius: 1,
                      position: 'relative',
                      overflow: 'hidden',
                      opacity: isLowStock ? 0.9 : 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gridRow: { lg: span2 ? 'span 2' : 'span 1' },
                      minHeight: { xs: 140, lg: 'auto' },
                      '&:hover': {
                        borderColor: isLowStock ? stockStatus.color : '#2C3E2D',
                        '& .add-icon': { opacity: 1 },
                      },
                    }}
                  >
                    <Box
                      className="add-icon"
                      onClick={e => {
                        e.stopPropagation();
                        handleProductClick(product);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        opacity: 0,
                        transform: 'scale(0.8)',
                        transition: 'all 0.2s ease',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: isLowStock ? '#F44336' : ACCENT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFF',
                        boxShadow: '0 2px 8px rgba(44,62,45,0.25)',
                        zIndex: 1,
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            bgcolor: isLowStock ? 'rgba(244,67,54,0.08)' : '#F0EDE9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={
                              product.category?.category_type === 'beverage'
                                ? '/icons/beverage.png'
                                : product.category?.category_type === 'merchandise'
                                  ? '/icons/merchandise.png'
                                  : product.category?.category_type === 'service'
                                    ? '/icons/services.png'
                                    : '/icons/kitchen.png'
                            }
                            alt=""
                            sx={{ width: 22, height: 22, objectFit: 'contain' }}
                          />
                        </Box>
                        <Box
                          className="add-icon"
                          onClick={e => {
                            e.stopPropagation();
                            handleProductClick(product);
                          }}
                          sx={{
                            opacity: 0,
                            transition: 'all 0.2s ease',
                            width: 28,
                            height: 28,
                            borderRadius: 0,
                            bgcolor: isLowStock ? '#F44336' : ACCENT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFF',
                          }}
                        >
                          <AddIcon sx={{ fontSize: 14 }} />
                        </Box>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: isTall ? '1rem' : '0.85rem',
                            fontWeight: 700,
                            color: '#0C0C0B',
                            lineHeight: 1.15,
                            mb: 0.5,
                          }}
                        >
                          {product.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            color: '#9B958F',
                            lineHeight: 1.4,
                          }}
                        >
                          {product.category?.name || 'Uncategorized'}
                          {isLowStock ? ` \u00b7 ${stockStatus.label}` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '1rem',
                              fontWeight: 700,
                              color: ACCENT,
                            }}
                          >
                            {formatCurrency(product.price)}
                          </Typography>
                          <Box
                            onClick={e => {
                              e.stopPropagation();
                              toggleAvailabilityMutation.mutate(product.id);
                            }}
                            sx={{
                              opacity: 0.4,
                              width: 16,
                              height: 16,
                              borderRadius: '3px',
                              border: `1px solid ${ACCENT}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 1,
                                bgcolor: 'rgba(211,47,47,0.08)',
                                borderColor: '#D32F2F',
                              },
                            }}
                            title="Toggle availability"
                          >
                            <WarningIcon sx={{ fontSize: 8, color: ACCENT }} />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Draggable Floating Panel */}
        <Box
          id="floating-panel"
          data-tour="cart-panel"
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            width: 500,
            bgcolor: '#FFF',
            border: BORDER,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 1000,
            cursor: isDragging ? 'grabbing' : 'auto',
          }}
        >
          <form onSubmit={handleSubmit(onCreateOrder)}>
            {/* Panel Header - Draggable */}
            <Box
              sx={{
                p: 1.5,
                bgcolor: '#FAFAF8',
                borderBottom: BORDER,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                touchAction: 'none',
              }}
              onPointerDown={handleDragStart}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DragIcon sx={{ fontSize: 16, color: '#9B958F' }} />
                  <Typography
                    sx={{
                      fontSize: '0.58rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#B0AAA2',
                      lineHeight: 1,
                    }}
                  >
                    Drag me
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: ACCENT,
                  }}
                >
                  Current Order
                </Typography>
                {items.length > 0 && (
                  <Chip
                    label={items.length}
                    size="small"
                    sx={{
                      bgcolor: ACCENT,
                      color: '#FFF',
                      borderRadius: 0,
                      height: 18,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              </Box>
              <Box
                component="button"
                type="button"
                aria-label={isPanelMinimized ? 'Maximize' : 'Minimize'}
                onPointerDown={event => event.stopPropagation()}
                onClick={() => setIsPanelMinimized(!isPanelMinimized)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  height: 26,
                  px: '8px',
                  mr: isPanelMinimized ? '0px' : 0,
                  flexShrink: 0,
                  border: '1px solid #E4DFD9',
                  bgcolor: '#FFF',
                  color: '#5C574F',
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  '&:hover': { bgcolor: '#F5F3F0', borderColor: '#C8C2BA' },
                }}
              >
                {isPanelMinimized ? (
                  <ExpandMoreIcon sx={{ fontSize: 14 }} />
                ) : (
                  <MinimizeIcon sx={{ fontSize: 14 }} />
                )}
                {isPanelMinimized ? 'Maximize' : 'Minimize'}
              </Box>
            </Box>

            {/* Panel Content */}
            {!isPanelMinimized && (
              <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '75vh' }}>
                {/* Order Type and all fields in one row */}
                <Box data-tour="order-type" sx={{ p: 1.5, borderBottom: BORDER }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Controller
                      name="orderType"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small" sx={{ width: 120 }}>
                          <Select
                            {...field}
                            sx={{
                              borderRadius: 0,
                              '& .MuiSelect-select': { py: 0.75, fontSize: '0.75rem' },
                            }}
                          >
                            <MenuItem value="dine_in">🍽️ Dine In</MenuItem>
                            <MenuItem value="takeout">📦 Takeout</MenuItem>
                            <MenuItem value="delivery">🚚 Delivery</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />

                    {watchedOrderType === 'dine_in' && (
                      <>
                        {roomChargeEnabled && (
                          <Controller
                            name="selectedBooking"
                            control={control}
                            render={({ field }) => (
                              <FormControl size="small" sx={{ width: 150 }}>
                                <Select
                                  {...field}
                                  displayEmpty
                                  sx={{
                                    borderRadius: 0,
                                    '& .MuiSelect-select': { py: 0.75, fontSize: '0.75rem' },
                                  }}
                                >
                                  <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                                    🛏️ Room
                                  </MenuItem>
                                  {activeBookings.map(booking => (
                                    <MenuItem
                                      key={booking.id}
                                      value={booking.id}
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {booking.room?.room_number || 'Room'} —{' '}
                                      {booking.guest?.first_name} {booking.guest?.last_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          />
                        )}
                        <Controller
                          name="selectedTable"
                          control={control}
                          render={({ field }) => (
                            <FormControl size="small" sx={{ width: 120 }}>
                              <Select
                                {...field}
                                displayEmpty
                                sx={{
                                  borderRadius: 0,
                                  '& .MuiSelect-select': { py: 0.75, fontSize: '0.75rem' },
                                }}
                              >
                                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                                  Table
                                </MenuItem>
                                {tables.map(table => (
                                  <MenuItem
                                    key={table.id}
                                    value={table.id}
                                    sx={{ fontSize: '0.75rem' }}
                                  >
                                    {table.display_name || table.table_number}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                        <Controller
                          name="guestCount"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              type="number"
                              size="small"
                              placeholder="Guests"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PeopleIcon sx={{ fontSize: 14, color: '#9B958F' }} />
                                  </InputAdornment>
                                ),
                                inputProps: { min: 1, max: 20 },
                              }}
                              sx={{
                                width: 90,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 0,
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          )}
                        />
                      </>
                    )}

                    {watchedOrderType === 'delivery' && (
                      <Controller
                        name="deliveryAddress"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            size="small"
                            placeholder="Delivery address"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon sx={{ fontSize: 14, color: '#9B958F' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              flex: 1,
                              '& .MuiOutlinedInput-root': { borderRadius: 0, fontSize: '0.75rem' },
                            }}
                          />
                        )}
                      />
                    )}

                    <Controller
                      name="guestName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          placeholder={
                            watchedOrderType === 'delivery' ? 'Recipient name' : 'Guest name'
                          }
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': { borderRadius: 0, fontSize: '0.75rem' },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>

                {/* Cart Items with Notes */}
                <Box
                  data-tour="cart-items"
                  sx={{ flex: 1, overflow: 'auto', p: 1.5, minHeight: 200, maxHeight: 300 }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: '#9B958F',
                      mb: 1,
                      textTransform: 'uppercase',
                    }}
                  >
                    Order Items ({items.length})
                  </Typography>
                  {items.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CartIcon sx={{ fontSize: 40, color: '#E2DDD8' }} />
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#9B958F',
                          mt: 1,
                        }}
                      >
                        No items added
                      </Typography>
                    </Box>
                  ) : (
                    items.map(item => {
                      const itemWithNote = item as CartItemWithNote;
                      return (
                        <Paper
                          key={item.id}
                          elevation={0}
                          sx={{
                            mb: 1,
                            p: 1,
                            border: BORDER,
                            borderRadius: 0,
                            bgcolor: itemWithNote.note ? '#FFF8F0' : '#FFF',
                            transition: 'all 0.2s',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                }}
                              >
                                {item.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.7rem',
                                  color: '#9B958F',
                                }}
                              >
                                {formatCurrency(item.price)}
                              </Typography>
                              {itemWithNote.note && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mt: 0.5,
                                    p: 0.5,
                                    bgcolor: '#FFF3E0',
                                  }}
                                >
                                  <NotesIcon sx={{ fontSize: 10, color: '#ED6C02' }} />
                                  <Typography
                                    sx={{
                                      fontSize: '0.6rem',
                                      color: '#ED6C02',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {itemWithNote.note}
                                  </Typography>
                                </Box>
                              )}
                              {itemWithNote.modifiers && itemWithNote.modifiers.length > 0 && (
                                <Box sx={{ mt: 0.5 }}>
                                  {itemWithNote.modifiers.map(group =>
                                    group.options.map((opt, oi) => (
                                      <Box
                                        key={`${group.groupName}-${opt.name}-${oi}`}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          py: 0.25,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: '0.6rem',
                                            color: '#6B6560',
                                          }}
                                        >
                                          + {group.groupName}: {opt.name}
                                          {opt.price_modifier > 0 && (
                                            <Box
                                              component="span"
                                              sx={{ color: ACCENT, fontWeight: 600, ml: 0.5 }}
                                            >
                                              {formatCurrency(opt.price_modifier)}
                                            </Box>
                                          )}
                                        </Typography>
                                      </Box>
                                    )),
                                  )}
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={e => handleOpenNotePopover(e, itemWithNote)}
                                sx={{
                                  p: 0.5,
                                  color: itemWithNote.note ? '#ED6C02' : '#9B958F',
                                }}
                              >
                                <NoteAddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                sx={{ p: 0.5 }}
                              >
                                <RemoveIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <Typography
                                sx={{ fontSize: '0.75rem', minWidth: 20, textAlign: 'center' }}
                              >
                                {item.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                sx={{ p: 0.5 }}
                              >
                                <AddIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => removeItem(item.id)}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: 14, color: '#F44336' }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })
                  )}

                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Order Notes"
                        multiline
                        rows={2}
                        placeholder="Special requests, allergies, etc. for the entire order"
                        sx={{
                          mt: 1.5,
                          '& .MuiOutlinedInput-root': { borderRadius: 0, fontSize: '0.75rem' },
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Summary & Action */}
                <Box sx={{ p: 1.5, borderTop: BORDER, bgcolor: '#FAFAF8' }}>
                  {/* Add to Existing Order Section */}
                  <Accordion
                    expanded={addToExistingOrder}
                    onChange={() => {
                      setAddToExistingOrder(!addToExistingOrder);
                      if (!addToExistingOrder) {
                        setSelectedExistingOrderId('');
                        setExistingOrderDetails(null);
                        setOrderSearchQuery('');
                      }
                    }}
                    sx={{
                      mb: 1.5,
                      border: BORDER,
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      borderRadius: 0,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={addToExistingOrder ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{
                        bgcolor: '#FFF',
                        minHeight: 40,
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          transform: 'none !important',
                        },
                        '& .MuiAccordionSummary-content': { my: 1 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: ACCENT,
                        }}
                      >
                        Add to Existing Order
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1.5, pt: 0 }}>
                      <Autocomplete
                        options={filteredIncompleteOrders}
                        getOptionLabel={option =>
                          `#${option.order_number} - ${option.guest_name || 'Guest'}`
                        }
                        value={incompleteOrders.find(o => o.id === selectedExistingOrderId) || null}
                        onChange={(_, newValue) => {
                          handleExistingOrderChange(newValue?.id || '');
                        }}
                        onInputChange={(_, newInputValue) => {
                          setOrderSearchQuery(newInputValue);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Search orders by number or guest name..."
                            sx={{
                              '& .MuiOutlinedInput-root': { borderRadius: 0, fontSize: '0.75rem' },
                            }}
                          />
                        )}
                        sx={{ '& .MuiAutocomplete-inputRoot': { py: 0.5 } }}
                      />

                      {/* Simplified Order Summary */}
                      {existingOrderDetails && (
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#FAFAF8', border: BORDER }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                color: '#9B958F',
                              }}
                            >
                              Order #{existingOrderDetails.order_number}
                            </Typography>
                            <Chip
                              label={existingOrderDetails.status}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.55rem',
                                bgcolor: '#E2DDD8',
                                borderRadius: 0,
                              }}
                            />
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#6B6560' }}>
                              Current Total
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: ACCENT }}>
                              {formatCurrency(existingOrderCurrentTotal)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#6B6560' }}>
                              New Total After Addition
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: ACCENT }}>
                              {formatCurrency(newTotalAfterAddition)}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ color: '#9B958F', display: 'block', mt: 1, fontStyle: 'italic' }}
                          >
                            Current items + {items.length} new item{items.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Subtotal, Tax, Total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem' }}>
                      {addingToLegacyOrder ? 'Subtotal' : 'Subtotal (incl. VAT)'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem' }}>{formatCurrency(subtotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.7rem' }}>
                      {addingToLegacyOrder ? 'VAT' : 'VAT included'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem' }}>{formatCurrency(taxAmount)}</Typography>
                  </Box>
                  {!addingToLegacyOrder && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: '0.7rem' }}>Service charge 10%</Typography>
                      <Typography sx={{ fontSize: '0.7rem' }}>
                        {formatCurrency(serviceCharge)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Cart Total</Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                      {formatCurrency(totalAmount)}
                    </Typography>
                  </Box>

                  {watchedOrderType === 'dine_in' && watchedBooking && roomChargeEnabled && (
                    <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        type="checkbox"
                        id="chargeToRoom"
                        checked={chargeToRoom}
                        onChange={e => setChargeToRoom(e.target.checked)}
                        style={{ accentColor: ACCENT }}
                      />
                      <Box component="label" htmlFor="chargeToRoom" sx={{ cursor: 'pointer' }}>
                        <Typography
                          sx={{
                            fontSize: '0.72rem',
                            color: ACCENT,
                            fontWeight: 600,
                          }}
                        >
                          Charge to Room Folio
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            color: '#9B958F',
                          }}
                        >
                          Order will be added to the guest's room bill
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Action Button */}
                  <Box data-tour="create-order-btn">
                    {addToExistingOrder ? (
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={
                          items.length === 0 ||
                          !selectedExistingOrderId ||
                          addItemToOrderMutation.isPending
                        }
                        onClick={onAddToExistingOrder}
                        startIcon={<AddIcon />}
                        sx={{
                          bgcolor: ACCENT,
                          borderRadius: 2,
                          py: 1,
                          px: 3,
                          fontSize: '0.78rem',

                          fontWeight: 600,
                          letterSpacing: '0.02em',
                          boxShadow: '0 2px 12px rgba(44,62,45,0.25)',
                          '&:hover': {
                            bgcolor: '#1a2d1b',
                            boxShadow: '0 4px 16px rgba(44,62,45,0.35)',
                          },
                          '&:disabled': { bgcolor: '#E2DDD8', color: '#9B958F', boxShadow: 'none' },
                        }}
                      >
                        {addItemToOrderMutation.isPending ? 'Adding...' : 'Add Items to Order'}
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={items.length === 0 || createOrderMutation.isPending}
                        startIcon={<ReceiptIcon />}
                        sx={{
                          bgcolor: ACCENT,
                          borderRadius: 2,
                          py: 1,
                          px: 3,
                          fontSize: '0.78rem',

                          fontWeight: 600,
                          letterSpacing: '0.02em',
                          boxShadow: '0 2px 12px rgba(44,62,45,0.25)',
                          '&:hover': {
                            bgcolor: '#1a2d1b',
                            boxShadow: '0 4px 16px rgba(44,62,45,0.35)',
                          },
                          '&:disabled': { bgcolor: '#E2DDD8', color: '#9B958F', boxShadow: 'none' },
                        }}
                      >
                        {createOrderMutation.isPending ? 'Creating...' : 'Create Order'}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </form>
        </Box>

        {/* Note Popover */}
        <Popover
          open={Boolean(noteAnchorEl)}
          anchorEl={noteAnchorEl}
          onClose={handleCloseNotePopover}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              borderRadius: 0,
              border: BORDER,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              width: 280,
            },
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: ACCENT,
                }}
              >
                Add Note
              </Typography>
              <IconButton size="small" onClick={handleCloseNotePopover}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="e.g., Extra spicy, No onions, Allergies..."
              value={tempNote}
              onChange={e => setTempNote(e.target.value)}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': { borderRadius: 0, fontSize: '0.75rem' },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleCloseNotePopover}
                sx={{ borderRadius: 0, fontSize: '0.7rem' }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                onClick={handleSaveNote}
                sx={{
                  bgcolor: ACCENT,
                  borderRadius: 0,

                  fontSize: '0.7rem',
                }}
              >
                Save Note
              </Button>
            </Box>
          </Box>
        </Popover>
        {modifierProduct && (
          <ModifierPicker
            open={modifierPickerOpen}
            onClose={() => {
              setModifierPickerOpen(false);
              setModifierProduct(null);
            }}
            onConfirm={handleModifierConfirm}
            productName={modifierProduct.name}
            productBasePrice={modifierProduct.price}
            modifierGroups={(modifierProduct.modifier_groups as ModifierGroup[]) || []}
          />
        )}
      </PageSection>
    </Box>
  );
}

export default OrdersPage;
